import { queryClient } from '@/plugins/tanstack'
import {
  SERVER_DATA_LABELS,
  type ServerDataScope,
  type ServerDataScopeLabels,
  SERVER_DATA_SCOPE,
  type ServerDataScopeParams,
  SERVER_DATA_TYPE,
} from '@/common/types/cacheConstants'

export type InvalidationScopeServerData = ReturnType<typeof queryKeyFactory>[]
export const invalidateQuery = (scopes: InvalidationScopeServerData) => {
  scopes.forEach((keys) => {
    const scope = keys[0] as ServerDataScope

    if (!queryClient.isFetching({ queryKey: keys })) {
      queryClient.removeQueries({ queryKey: keys })
    }

    if (scope in SERVER_DATA_LABELS === false) return

    SERVER_DATA_LABELS[scope as ServerDataScopeLabels].forEach((depPrefix) => {
      const queryKey = queryKeyFactory({
        scope: SERVER_DATA_SCOPE.I18n,
        type: SERVER_DATA_TYPE.ITEM,
        params: depPrefix,
      })
      if (!queryClient.isFetching({ queryKey: queryKey })) {
        queryClient.removeQueries({ queryKey: queryKey })
      }
    })
  })
}

type TypeValues = keyof typeof SERVER_DATA_TYPE

type TypedScopeParams<S extends keyof ServerDataScopeParams, T extends TypeValues> = T extends keyof ServerDataScopeParams[S]
  ? ServerDataScopeParams[S][T] | undefined
  : undefined

type ScopeWithTypeAndParams<S extends keyof ServerDataScopeParams, T extends TypeValues> = {
  scope: S
  type: T
  params?: TypedScopeParams<S, T>
}

type ScopeWithParamsOnly<S extends keyof ServerDataScopeParams> = {
  scope: S
  type?: never
  params?: ServerDataScopeParams[S]
}

type ScopeWithTypeOnly<S extends ServerDataScope, T extends TypeValues> = {
  scope: S
  type: T
  params?: never
}

type ScopeOnly<S extends ServerDataScope> = {
  scope: S
  type?: never
  params?: never
}

type QueryKeyFactoryOptions<S extends ServerDataScope, T extends TypeValues | undefined> = S extends keyof ServerDataScopeParams
  ? T extends TypeValues
    ? ScopeWithTypeAndParams<S, T>
    : ScopeWithParamsOnly<S>
  : T extends TypeValues
    ? ScopeWithTypeOnly<S, T>
    : ScopeOnly<S>

export const queryKeyFactory = <S extends ServerDataScope, T extends TypeValues | undefined>({
  type,
  params,
  scope,
}: QueryKeyFactoryOptions<S, T>) => {
  if (type && params) {
    return [scope, type, params]
  } else if (params) {
    return [scope, params]
  } else if (type) {
    return [scope, type]
  } else {
    return [scope]
  }
}
